import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSnoozedChefs,
  snoozeChef,
  unsnoozeChef,
} from '../services/notificationMuteService';

const MUTES_KEY = ['notification-mutes'] as const;

/**
 * Lists the caller's snoozed chefs (per-chef new-dish mutes). The count for the
 * settings "Snoozed chefs (N)" row is just `data?.length`.
 */
export function useSnoozedChefs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: MUTES_KEY,
    queryFn: getSnoozedChefs,
    enabled: options?.enabled ?? true,
  });
}

/**
 * "Snooze this chef." Idempotent server-side. Invalidates the snoozed list so
 * the settings count/list reflect the new mute.
 */
export function useSnoozeChef() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mutedProfileId: string) => snoozeChef(mutedProfileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MUTES_KEY });
    },
  });
}

/**
 * Un-snooze a chef. Idempotent server-side. Invalidates the snoozed list so the
 * row drops out of the settings management view.
 */
export function useUnsnoozeChef() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mutedProfileId: string) => unsnoozeChef(mutedProfileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MUTES_KEY });
    },
  });
}
